import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MarketAlerts = () => {
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [alertForm, setAlertForm] = useState({
    category: '',
    condition: 'price_above',
    value: '',
    frequency: 'immediate'
  });

  const activeAlerts = [
    {
      id: 1,
      name: "Rolex Submariner Price Alert",
      category: "Vintage Watches",
      condition: "Price above $15,000",
      frequency: "Daily",
      status: "active",
      triggered: 3,
      created: "2024-09-15"
    },
    {
      id: 2,
      name: "Michael Jordan Rookie Cards",
      category: "Sports Cards",
      condition: "New listings with PSA 10",
      frequency: "Immediate",
      status: "active",
      triggered: 12,
      created: "2024-09-20"
    },
    {
      id: 3,
      name: "Marvel Comics Golden Age",
      category: "Comic Books",
      condition: "Price drop below $5,000",
      frequency: "Weekly",
      status: "paused",
      triggered: 0,
      created: "2024-09-10"
    },
    {
      id: 4,
      name: "Picasso Prints Market",
      category: "Art & Antiques",
      condition: "Volume increase >50%",
      frequency: "Daily",
      status: "active",
      triggered: 7,
      created: "2024-09-05"
    }
  ];

  const recentTriggers = [
    {
      id: 1,
      alertName: "Rolex Submariner Price Alert",
      message: "3 new listings found above $15,000",
      timestamp: "2 hours ago",
      type: "price",
      priority: "high"
    },
    {
      id: 2,
      alertName: "Michael Jordan Rookie Cards",
      message: "PSA 10 1986 Fleer Jordan listed at $45,000",
      timestamp: "4 hours ago",
      type: "listing",
      priority: "high"
    },
    {
      id: 3,
      alertName: "Picasso Prints Market",
      message: "Trading volume increased by 67% today",
      timestamp: "6 hours ago",
      type: "volume",
      priority: "medium"
    },
    {
      id: 4,
      alertName: "Vintage Watch Market",
      message: "Average prices up 3.2% this week",
      timestamp: "1 day ago",
      type: "trend",
      priority: "low"
    }
  ];

  const handleCreateAlert = (e) => {
    e?.preventDefault();
    // Handle alert creation logic here
    setShowCreateAlert(false);
    setAlertForm({
      category: '',
      condition: 'price_above',
      value: '',
      frequency: 'immediate'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'paused': return 'text-warning bg-warning/10';
      case 'inactive': return 'text-muted-foreground bg-muted/30';
      default: return 'text-muted-foreground bg-muted/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive bg-destructive/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'low': return 'text-muted-foreground bg-muted/30';
      default: return 'text-muted-foreground bg-muted/30';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'price': return 'DollarSign';
      case 'listing': return 'Package';
      case 'volume': return 'BarChart3';
      case 'trend': return 'TrendingUp';
      default: return 'Bell';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-foreground">Market Alerts</h2>
          <p className="text-sm text-muted-foreground">Stay informed about market movements and opportunities</p>
        </div>
        
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateAlert(true)}
        >
          Create Alert
        </Button>
      </div>
      {/* Create Alert Modal */}
      {showCreateAlert && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-card-foreground">Create New Alert</h3>
              <button
                onClick={() => setShowCreateAlert(false)}
                className="p-2 hover:bg-muted/50 rounded-lg transition-colors duration-200"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            <form onSubmit={handleCreateAlert} className="space-y-4">
              <Input
                label="Alert Name"
                type="text"
                placeholder="Enter alert name"
                required
              />

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Category</label>
                <select className="w-full px-3 py-2 bg-input border border-border rounded-lg text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Select category</option>
                  <option value="watches">Vintage Watches</option>
                  <option value="cards">Sports Cards</option>
                  <option value="comics">Comic Books</option>
                  <option value="art">Art & Antiques</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Condition</label>
                <select className="w-full px-3 py-2 bg-input border border-border rounded-lg text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="price_above">Price above</option>
                  <option value="price_below">Price below</option>
                  <option value="new_listing">New listing</option>
                  <option value="volume_increase">Volume increase</option>
                  <option value="price_drop">Price drop</option>
                </select>
              </div>

              <Input
                label="Value"
                type="text"
                placeholder="Enter threshold value"
                required
              />

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Frequency</label>
                <select className="w-full px-3 py-2 bg-input border border-border rounded-lg text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="immediate">Immediate</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => setShowCreateAlert(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                >
                  Create Alert
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Recent Triggers */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Recent Triggers</h3>
          <button className="text-sm text-primary hover:text-primary/80 transition-colors duration-200">
            View All
          </button>
        </div>

        <div className="space-y-3">
          {recentTriggers?.map((trigger) => (
            <div
              key={trigger?.id}
              className="flex items-center space-x-4 p-3 bg-muted/20 rounded-lg premium-hover"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getPriorityColor(trigger?.priority)}`}>
                <Icon name={getTypeIcon(trigger?.type)} size={20} />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-card-foreground truncate">
                  {trigger?.alertName}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {trigger?.message}
                </p>
              </div>
              
              <div className="text-right">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(trigger?.priority)}`}>
                  {trigger?.priority}
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  {trigger?.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Active Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Active Alerts</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {activeAlerts?.filter(alert => alert?.status === 'active')?.length} active
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {activeAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 p-4 bg-muted/20 rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-card-foreground">{alert?.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(alert?.status)}`}>
                    {alert?.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                  <span>{alert?.category}</span>
                  <span>{alert?.condition}</span>
                  <span>Frequency: {alert?.frequency}</span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-card-foreground">{alert?.triggered}</p>
                  <p className="text-xs text-muted-foreground">Triggered</p>
                </div>
                
                <div className="flex space-x-2">
                  <button className="p-2 text-muted-foreground hover:text-card-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200">
                    <Icon name="Edit" size={16} />
                  </button>
                  <button className="p-2 text-muted-foreground hover:text-card-foreground hover:bg-muted/50 rounded-lg transition-colors duration-200">
                    <Icon name={alert?.status === 'active' ? 'Pause' : 'Play'} size={16} />
                  </button>
                  <button className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors duration-200">
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketAlerts;